import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * TrialNudgeBanner — a calm yellow strip on top of the dashboard that
 * surfaces only for free-tier families, only on visits 1-3, and only
 * when they're still eligible for the 14-day Plus trial.
 *
 * Why three visits and not one: families landing on FamFeel for the
 * first time are usually test-driving — they don't want a sales pitch
 * before they've added their kids. A single banner on day 1 is easy
 * to miss; nagging forever feels like Cozi-style "calendar limited"
 * pressure. Three visits is the sweet spot — they've come back, the
 * tool is sticking, NOW the gentle "btw, 14 days free" is welcome
 * rather than pushy.
 *
 * Tracking is per-device localStorage (`ff_trial_nudge_visits`) — same
 * mental model as the device-local "what's new" calendar dot. No
 * server state, so a family that uses two phones gets up to three
 * nudges per phone, which is fine; it's a soft prompt, not a quota.
 */
export default function TrialNudgeBanner({ onTryPlus }) {
  const { t } = useI18n();
  // null while we figure out whether to show; false = decided not to;
  // true = visible. Avoids a layout shift if billingStatus is slow.
  const [show, setShow] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // Read & bump the visit counter immediately — even if we end up
    // not showing the banner (e.g. they're on Plus already), counting
    // the visit means once they DO drop back to free, they don't get
    // 3 fresh nudges.
    let visits = 0;
    try {
      visits = parseInt(localStorage.getItem("ff_trial_nudge_visits") || "0", 10) || 0;
      visits += 1;
      localStorage.setItem("ff_trial_nudge_visits", String(visits));
    } catch { /* private mode → ignore, banner just won't persist */ }

    // Sticky-dismiss flag — once a family taps "Maybe later" we never
    // show this again on this device, even if their visit count is
    // still <=3. We don't tie this to backend state; it's a UI-level
    // "I heard you the first time".
    const dismissedForever = (() => {
      try { return localStorage.getItem("ff_trial_nudge_dismissed") === "1"; }
      catch { return false; }
    })();

    if (visits > 3 || dismissedForever) {
      setShow(false);
      return;
    }

    api.billingStatus()
      .then((s) => {
        if (!alive) return;
        const isFree = (s?.tier || "free") === "free";
        const eligible = !!s?.plus_trial_eligible;
        // Only nudge free-tier families who are still eligible. Plus /
        // Grand / Extended subscribers never see this; neither do
        // anyone who's already used their trial.
        setShow(isFree && eligible);
      })
      .catch(() => setShow(false));
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem("ff_trial_nudge_dismissed", "1"); } catch {}
    setShow(false);
  };

  const tryPlus = () => {
    setBusy(true);
    try { localStorage.setItem("ff_trial_nudge_dismissed", "1"); } catch {}
    if (onTryPlus) onTryPlus();
    // Component unmounts when parent re-renders the dashboard with
    // the billing sheet open, so we don't bother resetting `busy`.
  };

  if (!show) return null;

  return (
    <div
      className="mb-4 rounded-[12px] border border-[color:var(--accent-warm,#e6d8a8)] bg-[color:var(--bg-warm,#fbf6e9)] px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-3 flex-wrap"
      data-testid="trial-nudge-banner"
    >
      <Sparkles className="w-4 h-4 text-[color:var(--ink-2)] shrink-0" aria-hidden="true" />
      <div className="text-[13px] leading-snug text-[color:var(--ink)] flex-1 min-w-[200px]">
        <strong className="font-display tracking-tight">
          {t({ en: "Try Plus free for 14 days", da: "Prøv Plus gratis i 14 dage" })}
        </strong>
        {" "}
        <span className="text-[color:var(--ink-2)]">
          {t({
            en: "— extra members, custom photo avatars, unlimited events.",
            da: "— flere medlemmer, egne fotoavatars, ubegrænsede begivenheder.",
          })}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={dismiss}
          className="ff-btn text-[12px] px-2.5 py-1"
          data-testid="trial-nudge-dismiss"
        >
          {t({ en: "Maybe later", da: "Måske senere" })}
        </button>
        <button
          type="button"
          onClick={tryPlus}
          disabled={busy}
          className="ff-btn ff-btn-primary text-[12px] px-3 py-1"
          data-testid="trial-nudge-cta"
        >
          {busy
            ? t({ en: "Opening…", da: "Åbner…" })
            : t({ en: "See plans", da: "Se planer" })}
        </button>
      </div>
    </div>
  );
}
